import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import UserActivityFeed from './UserActivityFeed';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Shield, 
  AlertTriangle,
  Trash2,
  Save,
  X,
  Eye,
  RefreshCw,
  FileImage,
  IdCard
} from 'lucide-react';

interface UserProfile {
  id: string;
  full_name: string;
  email: string;
  role: string;
  phone?: string;
  national_id?: string;
  kyc_status: string;
  created_at: string;
  gender?: string;
  address_country?: string;
  address_city?: string;
  address_area?: string;
  address_street?: string;
  address_building?: string;
  address_apt?: string;
  id_front_url?: string;
  id_back_url?: string;
  budget_max?: number;
  roommates_count?: number;
  expected_move_in?: string;
  property_ownership_type?: string;
  payout_method?: string;
  university_id?: string;
  whatsapp_contact_count?: number;
}

interface UserDetailsCardProps {
  user: UserProfile;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

const UserDetailsCard = ({ user, isOpen, onClose, onUpdate }: UserDetailsCardProps) => {
  const { profile } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(user.role);
  const [selectedKycStatus, setSelectedKycStatus] = useState(user.kyc_status);
  const [currentUser, setCurrentUser] = useState<UserProfile>(user);

  // Update local state when user prop changes
  useEffect(() => {
    setCurrentUser(user);
    setSelectedRole(user.role);
    setSelectedKycStatus(user.kyc_status);
  }, [user]);

  // Security: Only allow role changes if user is admin and not changing their own role
  const canChangeRole = profile && profile.role === 'admin' && profile.id !== user.id;

  const handleRoleUpdate = async () => {
    if (!canChangeRole || selectedRole === currentUser.role) return;

    setLoading(true);
    console.log('Updating user role:', { userId: user.id, newRole: selectedRole });

    try {
      // Use the new secure function for role updates
      const { error } = await supabase.rpc('update_user_role', {
        target_user_id: user.id,
        new_role: selectedRole
      });

      if (error) {
        console.error('Error updating role:', error);
        toast.error(t('role_update_failed', 'فشل في تحديث الدور', 'Failed to update role'));
        setSelectedRole(currentUser.role); // Reset to original
        return;
      }

      toast.success(t('role_updated', 'تم تحديث الدور', 'Role updated successfully'));
      onUpdate();
    } catch (error) {
      console.error('Exception updating role:', error);
      toast.error(t('role_update_failed', 'فشل في تحديث الدور', 'Failed to update role'));
      setSelectedRole(currentUser.role); // Reset to original
    } finally {
      setLoading(false);
    }
  };

  const handleKycUpdate = async () => {
    if (selectedKycStatus === currentUser.kyc_status) return;

    setLoading(true);
    console.log('Updating KYC status:', { userId: user.id, newStatus: selectedKycStatus });

    try {
      // Use the secure KYC update function with proper audit logging and notifications
      const { error } = await supabase.rpc('update_kyc_status', {
        target_user_id: user.id,
        new_status: selectedKycStatus,
        admin_notes: null // Add admin notes input field if needed
      });

      if (error) {
        console.error('Error updating KYC status:', error);
        toast.error(t('kyc_update_failed', 'فشل في تحديث حالة التحقق', 'Failed to update KYC status'));
        setSelectedKycStatus(currentUser.kyc_status); // Reset to original
        return;
      }

      toast.success(t('kyc_updated', 'تم تحديث حالة التحقق', 'KYC status updated successfully'));
      onUpdate();
    } catch (error) {
      console.error('Exception updating KYC:', error);
      toast.error(t('kyc_update_failed', 'فشل في تحديث حالة التحقق', 'Failed to update KYC status'));
      setSelectedKycStatus(currentUser.kyc_status); // Reset to original
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!profile || profile.role !== 'admin' || profile.id === user.id) {
      toast.error(t('unauthorized', 'غير مصرح', 'Unauthorized'));
      return;
    }

    if (!confirm(t('confirm_delete_user', 'هل أنت متأكد من حذف هذا المستخدم؟', 'Are you sure you want to delete this user?'))) {
      return;
    }

    setLoading(true);
    console.log('Deleting user:', user.id);

    try {
      // Use the secure delete function
      const { error } = await supabase.rpc('delete_user_account', {
        target_user_id: user.id
      });

      if (error) {
        console.error('Error deleting user:', error);
        toast.error(t('user_delete_failed', 'فشل في حذف المستخدم', 'Failed to delete user'));
        return;
      }

      toast.success(t('user_deleted', 'تم حذف المستخدم', 'User deleted successfully'));
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Exception deleting user:', error);
      toast.error(t('user_delete_failed', 'فشل في حذف المستخدم', 'Failed to delete user'));
    } finally {
      setLoading(false);
    }
  };

  const extractStoragePath = (url: string): string => {
    if (!url) return '';
    
    // Extract path from full Supabase URL
    const match = url.match(/\/storage\/v1\/object\/public\/kyc-documents\/(.+)$/);
    return match ? match[1] : url;
  };

  const viewDocument = async (url: string) => {
    if (!url) return;
    
    try {
      // Extract the storage path from the URL
      const storagePath = extractStoragePath(url);
      
      // Generate a signed URL for the private bucket
      const { data, error } = await supabase.storage
        .from('kyc-documents')
        .createSignedUrl(storagePath, 3600); // 1 hour expiry

      if (error) {
        console.error('Error creating signed URL:', error);
        throw error;
      }
      
      window.open(data.signedUrl, '_blank');
    } catch (error) {
      console.error('Error viewing document:', error);
      toast.error('Failed to view document');
    }
  };

  const DocumentThumbnail = ({ url, label }: { url?: string; label: string }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [imageLoading, setImageLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
      const loadImage = async () => {
        if (!url) {
          setImageLoading(false);
          return;
        }

        try {
          setImageLoading(true);
          setError(false);

          // Extract the storage path from the URL
          const storagePath = extractStoragePath(url);
          
          // Generate a signed URL for the private bucket
          const { data, error: storageError } = await supabase.storage
            .from('kyc-documents')
            .createSignedUrl(storagePath, 3600); // 1 hour expiry

          if (storageError) {
            console.error('Error creating signed URL:', storageError);
            setError(true);
          } else {
            setImageUrl(data.signedUrl);
          }
        } catch (err) {
          console.error('Error loading image:', err);
          setError(true);
        } finally {
          setImageLoading(false);
        }
      };

      loadImage();
    }, [url]);

    if (!url) {
      return (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center text-muted-foreground">
          {t('no_document_uploaded', 'لم يتم رفع وثيقة', 'No document uploaded')}
        </div>
      );
    }

    return (
      <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-white">
        {imageLoading ? (
          <div className="w-full h-40 flex items-center justify-center bg-gray-100">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="w-full h-40 flex flex-col items-center justify-center bg-red-50 text-red-600">
            <FileImage className="h-8 w-8 mb-2" />
            <span className="text-sm">Failed to load image</span>
          </div>
        ) : imageUrl ? (
          <div className="relative group">
            <img 
              src={imageUrl} 
              alt={label}
              className="w-full h-40 object-cover cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => viewDocument(url)}
              onError={() => setError(true)}
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
              <Button
                variant="secondary"
                size="sm"
                className="opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => viewDocument(url)}
              >
                <Eye className="h-4 w-4 mr-2" />
                {t('view_full_size', 'عرض بالحجم الكامل', 'View Full Size')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="w-full h-40 flex items-center justify-center bg-gray-100">
            <IdCard className="h-8 w-8 text-gray-400" />
          </div>
        )}
      </div>
    );
  };

  const formatAddress = () => {
    const parts = [
      currentUser.address_apt,
      currentUser.address_building,
      currentUser.address_street,
      currentUser.address_area,
      currentUser.address_city,
      currentUser.address_country
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : t('not_provided', 'غير محدد', 'Not provided');
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin': return 'destructive';
      case 'landlord': return 'default';
      case 'student':
      case 'user': return 'outline';
      default: return 'outline';
    }
  };

  const getKycBadgeVariant = (status: string) => {
    switch (status) {
      case 'VERIFIED': return 'default';
      case 'PENDING': return 'secondary';
      case 'REJECTED': return 'destructive';
      default: return 'outline';
    }
  };

  if (!profile || profile.role !== 'admin') {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {t('user_details', 'تفاصيل المستخدم', 'User Details')}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - User details (takes 2/3 of space) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('basic_info', 'المعلومات الأساسية', 'Basic Information')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div>
                   <label className="text-sm font-medium text-muted-foreground">
                     {t('full_name', 'الاسم الكامل', 'Full Name')}
                   </label>
                   <p className="text-sm">{currentUser.full_name || t('not_provided', 'غير محدد', 'Not provided')}</p>
                 </div>
                 <div>
                   <label className="text-sm font-medium text-muted-foreground">
                     {t('email', 'البريد الإلكتروني', 'Email')}
                   </label>
                   <p className="text-sm flex items-center gap-2">
                     <Mail className="h-4 w-4" />
                     {currentUser.email}
                   </p>
                 </div>
                 <div>
                   <label className="text-sm font-medium text-muted-foreground">
                     {t('phone', 'رقم الهاتف', 'Phone')}
                   </label>
                   <p className="text-sm flex items-center gap-2">
                     <Phone className="h-4 w-4" />
                     {currentUser.phone || t('not_provided', 'غير محدد', 'Not provided')}
                   </p>
                 </div>
                 <div>
                   <label className="text-sm font-medium text-muted-foreground">
                     {t('gender', 'الجنس', 'Gender')}
                   </label>
                   <p className="text-sm">{currentUser.gender || t('not_provided', 'غير محدد', 'Not provided')}</p>
                 </div>
                 <div>
                   <label className="text-sm font-medium text-muted-foreground">
                     {t('joined_date', 'تاريخ الانضمام', 'Joined Date')}
                   </label>
                   <p className="text-sm flex items-center gap-2">
                     <Calendar className="h-4 w-4" />
                     {new Date(currentUser.created_at).toLocaleDateString()}
                   </p>
                 </div>
                 <div>
                   <label className="text-sm font-medium text-muted-foreground">
                     {t('national_id', 'الرقم القومي', 'National ID')}
                   </label>
                   <p className="text-sm">
                     {currentUser.national_id ? '***' + currentUser.national_id.slice(-4) : t('not_provided', 'غير محدد', 'Not provided')}
                   </p>
                 </div>
              </div>
            </CardContent>
          </Card>

          {/* Address Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                {t('address', 'العنوان', 'Address')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{formatAddress()}</p>
            </CardContent>
          </Card>

          {/* KYC Documents Section */}
          {(currentUser.id_front_url || currentUser.id_back_url) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {t('submitted_documents', 'الوثائق المقدمة', 'Submitted Documents')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* National ID Front */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-muted-foreground">
                        {t('national_id_front', 'الهوية الأمامية', 'National ID Front')}
                      </h4>
                      {currentUser.id_front_url && (
                        <Badge variant="secondary" className="text-green-600 bg-green-50">
                          {t('uploaded', 'مرفوعة', 'Uploaded')}
                        </Badge>
                      )}
                    </div>
                    <DocumentThumbnail 
                      url={currentUser.id_front_url} 
                      label={t('front_id', 'الهوية الأمامية', 'Front ID')}
                    />
                  </div>

                  {/* National ID Back */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-muted-foreground">
                        {t('national_id_back', 'الهوية الخلفية', 'National ID Back')}
                      </h4>
                      {currentUser.id_back_url && (
                        <Badge variant="secondary" className="text-green-600 bg-green-50">
                          {t('uploaded', 'مرفوعة', 'Uploaded')}
                        </Badge>
                      )}
                    </div>
                    <DocumentThumbnail 
                      url={currentUser.id_back_url} 
                      label={t('back_id', 'الهوية الخلفية', 'Back ID')}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Security Management */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5" />
                {t('security_management', 'إدارة الأمان', 'Security Management')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-2 block">
                    {t('current_role', 'الدور الحالي', 'Current Role')}
                  </label>
                   <Badge variant={getRoleBadgeVariant(currentUser.role)}>
                     {currentUser.role?.toUpperCase()}
                   </Badge>
                 </div>
                 <div>
                   <label className="text-sm font-medium text-muted-foreground mb-2 block">
                     {t('kyc_status', 'حالة التحقق', 'KYC Status')}
                   </label>
                   <Badge variant={getKycBadgeVariant(currentUser.kyc_status)}>
                     {currentUser.kyc_status}
                   </Badge>
                </div>
              </div>

              <Separator />

              {/* Role Update Section */}
              {canChangeRole && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-2 block">
                    {t('change_role', 'تغيير الدور', 'Change Role')}
                  </label>
                  <div className="flex gap-2">
                    <Select value={selectedRole} onValueChange={setSelectedRole}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">{t('user', 'مستخدم', 'User')}</SelectItem>
                        <SelectItem value="student">{t('student', 'طالب', 'Student')}</SelectItem>
                        <SelectItem value="landlord">{t('landlord', 'مالك', 'Landlord')}</SelectItem>
                        <SelectItem value="admin">{t('admin', 'مدير', 'Admin')}</SelectItem>
                      </SelectContent>
                    </Select>
                     <Button
                       onClick={handleRoleUpdate}
                       disabled={loading || selectedRole === currentUser.role}
                       size="sm"
                     >
                      <Save className="h-4 w-4 mr-2" />
                      {t('update_role', 'تحديث الدور', 'Update Role')}
                    </Button>
                  </div>
                </div>
              )}

              {/* KYC Update Section */}
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  {t('change_kyc_status', 'تغيير حالة التحقق', 'Change KYC Status')}
                </label>
                <div className="flex gap-2">
                  <Select value={selectedKycStatus} onValueChange={setSelectedKycStatus}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PENDING">{t('pending', 'قيد الانتظار', 'Pending')}</SelectItem>
                      <SelectItem value="VERIFIED">{t('verified', 'متحقق', 'Verified')}</SelectItem>
                      <SelectItem value="REJECTED">{t('rejected', 'مرفوض', 'Rejected')}</SelectItem>
                    </SelectContent>
                  </Select>
                   <Button
                     onClick={handleKycUpdate}
                     disabled={loading || selectedKycStatus === currentUser.kyc_status}
                     size="sm"
                   >
                    <Save className="h-4 w-4 mr-2" />
                    {t('update_kyc', 'تحديث التحقق', 'Update KYC')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          {profile.role === 'admin' && profile.id !== user.id && (
            <Card className="border-destructive">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-destructive">
                  <AlertTriangle className="h-5 w-5" />
                  {t('danger_zone', 'منطقة خطر', 'Danger Zone')}
                </CardTitle>
                <CardDescription>
                  {t('irreversible_actions', 'الإجراءات التالية لا يمكن التراجع عنها', 'The following actions cannot be undone')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="destructive"
                  onClick={handleDeleteUser}
                  disabled={loading}
                  className="w-full"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('delete_user_account', 'حذف حساب المستخدم', 'Delete User Account')}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column - User Activity Feed (takes 1/3 of space) */}
        <div className="lg:col-span-1">
          <UserActivityFeed 
            userId={currentUser.id} 
            userName={currentUser.full_name}
          />
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t">
        <Button variant="outline" onClick={onClose}>
          <X className="h-4 w-4 mr-2" />
          {t('close', 'إغلاق', 'Close')}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
  );
};

export default UserDetailsCard;
